import type { ReactNode } from 'react';
import { ChevronLeft, X } from 'lucide-react';

export interface SubPageHeaderProps {
  /** Centered title. Long titles wrap rather than ellipsize. */
  title: string;
  /** Back / close handler. */
  onBack: () => void;
  /**
   * Leading affordance. `back` (default) is the sub-page chevron; `close`
   * is for surfaces the user dismisses rather than navigates out of.
   */
  leading?: 'back' | 'close';
  /** Optional trailing control, rendered in the balancing right slot. */
  rightAction?: ReactNode;
}

/**
 * The one Settings sub-page header.
 *
 * Settings used to carry two of these: `SettingsSubPage`'s (16px semibold,
 * hairline divider) and `FullScreenSheet`'s (20px bold, no divider, title
 * ellipsized in a 44px-flanked centre slot, so "Frequently Asked Questions"
 * rendered as "Frequently Asked Ques…"). Kelvin's call on YAR-389 was one
 * consistent style, matching the Profile screen — that is this component,
 * and every sub-page surface now renders it instead of its own markup.
 *
 * YAR-230 then set what that one style IS, against Figma: the title is
 * 20px (`text-lg`) in the heading tone #1a1a1a (`text-text-heading`), and
 * the hairline under it is gone. QA reported the old 16px/#2f2f2f/divider
 * combination once per sub-page across all eight screens; because they all
 * render this component, it is a single fix. The divider removal is why
 * `bg-background` is load-bearing here — it is now the only thing keeping
 * the scrolled content from showing through the pinned bar.
 *
 * `sticky top-0` only actually pins because AppShell's `<main>` no longer
 * declares `overflow-y-auto`; see the note there.
 */
export function SubPageHeader({
  title,
  onBack,
  leading = 'back',
  rightAction,
}: SubPageHeaderProps) {
  const Icon = leading === 'close' ? X : ChevronLeft;

  return (
    <div className="sticky top-0 z-[100] bg-background">
      <div className="flex items-center justify-between gap-2 px-5 py-3">
        <button
          type="button"
          onClick={onBack}
          className="shrink-0 inline-flex items-center justify-center rounded-md p-1 text-muted-foreground hover:bg-muted transition-colors duration-150 cursor-pointer"
          aria-label={leading === 'close' ? 'Close' : 'Go back'}
        >
          {/* #5e5e5e (`muted-foreground`) at stroke 1.5 — the chevron of
              Figma's shared "Top Nav (Sticky)" (8315:72076). It was
              `foreground` #2f2f2f at lucide's default 2 (QA, YAR-180). */}
          <Icon
            size={24}
            strokeWidth={1.5}
          />
        </button>
        {/* No `truncate`: the reported defect WAS the ellipsis. On the
            narrowest supported viewport the longest title wraps to a
            second line instead of losing its tail. */}
        <h1 className="min-w-0 flex-1 text-center text-lg font-semibold text-text-heading break-words">
          {title}
        </h1>
        <div className="w-[32px] shrink-0 flex items-center justify-end">
          {rightAction}
        </div>
      </div>
    </div>
  );
}
